import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../firebase';
import { doc, getDocs, getDoc, collection, updateDoc, setDoc } from 'firebase/firestore';
import {
Button, Typography, Container, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, IconButton, Radio, RadioGroup, FormControl, FormControlLabel, useMediaQuery, useTheme 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../auth';
import { useNavigate } from 'react-router-dom';
import Invoice from './Invoice';
import RateSellerDialog from './RateSellerDialog';
import PublicProfile from '../pages/PublicProfile';
import Loading from '../components/Loading';
import BuyerDispute from './BuyerDispute';
import PaymentRequestedDialog from './PaymentRequestedDialog';
import MessageBox from '../components/MessageBox';
import ImageViewerDialog from './ImageViewerDialog';
import PurchaseList from './PurchaseList';
import OfferTracking from './OfferTracking';
import PaymentDetailsDialog from './PaymentDetailsDialog';

const BuyerPurchases = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate(); 
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('date'); 
  const [allCollapsed, setAllCollapsed] = useState(false); 
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [message, setMessage] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundPaymentMethod, setRefundPaymentMethod] = useState('');
  const [refundPaymentDetails, setRefundPaymentDetails] = useState({});
  const [selectedShippingOption, setSelectedShippingOption] = useState('');
  const [shippingCost, setShippingCost] = useState(0);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState('');
  const [paymentDetails, setPaymentDetails] = useState('');
  const [buyerAddress, setBuyerAddress] = useState({});
  const [usernames, setUsernames] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [open, setOpen] = useState(false);
  const [totalCost, setTotalCost] = useState(0);
  const [paymentFee, setPaymentFee] = useState(0);
  const [viewDisputeDialogOpen, setViewDisputeDialogOpen] = useState(false);
  const [messageBoxOpen, setMessageBoxOpen] = useState(false);
  const [markAsPaidDialogOpen, setMarkAsPaidDialogOpen] = useState(false);
  const [buyerPaymentMessage, setBuyerPaymentMessage] = useState('');
  const [messageBoxContent, setMessageBoxContent] = useState({ title: '', message: '' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [putBackInCartDialogOpen, setPutBackInCartDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [approveChangesDialogOpen, setApproveChangesDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [rateSellerDialogOpen, setRateSellerDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [paymentDetailsDialogOpen, setPaymentDetailsDialogOpen] = useState(false);


  const [openOfferTracking, setOpenOfferTracking] = useState(false);

  const handleCloseOfferTracking = () => setOpenOfferTracking(false);

  const handleOpenPaymentDetailsDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setPaymentDetails(purchase.paymentOption?.details || {});
    setPaymentDetailsDialogOpen(true);
  };
  
  const handleClosePaymentDetailsDialog = () => {
    setPaymentDetailsDialogOpen(false);
  };

  const handleNavigateToPurchaseSummary = () => {
    navigate('/purchase-summary');
  };
  

  const handleOpenCancelDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setCancelDialogOpen(true);
  };

  const handleOpenPutBackInCartDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setPutBackInCartDialogOpen(true);
  };
  
  const handleOpenRefundDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setRefundDialogOpen(true);
  };
  
  const handleOpenDisputeDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setDisputeDialogOpen(true);
  };
  
  const handleOpenInvoiceDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setInvoiceDialogOpen(true);
  };
  
  const handleOpenTrackingDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setTrackingDialogOpen(true);
  };

  const handleOpenApproveChangesDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setApproveChangesDialogOpen(true);
  };
  
  const handleOpenPaymentDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setPaymentDialogOpen(true);
  };

  const handleOpenProfile = (sellerId) => {
    setSelectedUserId(sellerId);
    setOpen(true);
  };

  const handleCancelPurchase = async () => {
    if (selectedPurchase) {
      try {
        // Directly call handlePutBackInCart to handle cancellation and cart update
        await handlePutBackInCart();
  
        // Close the cancel dialog and show a success message
        setCancelDialogOpen(false);
        setCancelReason('');
        setMessageBoxContent({
          title: 'Purchase Cancelled',
          message: 'The purchase has been successfully cancelled, and items have been put back in your cart.',
        });
        setMessageBoxOpen(true);
  
        // Refresh the purchases list
        fetchPurchases();
      } catch (error) {
        console.error('Error cancelling purchase:', error);
        setMessageBoxContent({
          title: 'Error',
          message: 'An error occurred while cancelling the purchase. Please try again.',
        });
        setMessageBoxOpen(true);
      }
    }
  };
  
  
  

  const handlePutBackInCart = async () => {
    try {
      if (!selectedPurchase.items || !Array.isArray(selectedPurchase.items)) {
        console.error('Invalid purchase items:', selectedPurchase.items);
        throw new Error('No items found in purchase or items is not an array');
      }
  
      const cartRef = doc(db, 'carts', currentUser.uid);
      const cartDoc = await getDoc(cartRef);
  
      let cartItems = [];
      if (cartDoc.exists()) {
        cartItems = cartDoc.data().items || [];
      }
  
      const updatedCartItems = [...cartItems];
  
      selectedPurchase.items.forEach((purchaseItem) => {
        const cartItem = {
          docName: purchaseItem.docName,
          setId: purchaseItem.setId,
          quantity: purchaseItem.quantity,
          price: purchaseItem.price,
          image_uris: { digital: { small: purchaseItem.cardImage } },
          name: purchaseItem.cardName,
          rarity: purchaseItem.cardRarity,
          ink: purchaseItem.cardInk,
          collector_number: purchaseItem.collector_number,
          listingId: purchaseItem.listingId,
          sellerId: purchaseItem.sellerId || purchaseItem.listingId, // Ensure sellerId is properly set
          type: 'listing',
          condition: purchaseItem.condition || 'Near Mint',
          listing: purchaseItem.listing,
        };
  
        const existingItemIndex = updatedCartItems.findIndex(
          (item) =>
            item.docName === purchaseItem.docName &&
            item.setId === purchaseItem.setId &&
            item.sellerId === purchaseItem.sellerId // Ensure matching sellerId
        );
  
        if (existingItemIndex >= 0) {
          updatedCartItems[existingItemIndex].quantity += purchaseItem.quantity;
        } else {
          updatedCartItems.push(cartItem);
        }
      });
  
      // Add the items to the cart in Firestore
      await setDoc(cartRef, { items: updatedCartItems });
  
      // Update the purchase status to 'Cancelled'
      const buyerPurchaseRef = doc(db, 'users', currentUser.uid, 'purchases', selectedPurchase.id);
      const sellerSaleRef = doc(db, 'users', selectedPurchase.sellerId, 'sales', selectedPurchase.id);
  
      await updateDoc(buyerPurchaseRef, { status: 'Cancelled' });
      await updateDoc(sellerSaleRef, { status: 'Cancelled' });
  
      setPutBackInCartDialogOpen(false);
      fetchPurchases();
  
      alert('Items have been successfully moved back to your cart and marked as cancelled.');
    } catch (error) {
      console.error('Error moving items to cart:', error);
      alert('There was an error moving items back to your cart.');
    }
  };
  
  
  
  
  
  



  useEffect(() => {
    if (selectedShippingOption && selectedPurchase) {
      const shippingOption = selectedPurchase.availableShippingOptions?.find(option => option.method === selectedShippingOption);
      if (shippingOption) {
        setShippingCost(parseFloat(shippingOption.chargePerItem.replace(/[^0-9.-]+/g, "")));
      } else {
        setShippingCost(0);
      }
    }
  }, [selectedShippingOption, selectedPurchase]);

  const handleOpenImageViewer = (purchase) => {
    setSelectedPurchase(purchase);
    setImageDialogOpen(true);
  };

  const handleCloseImageViewer = () => {
    setImageDialogOpen(false);
  };

  useEffect(() => {
    if (selectedPurchase) {
      const subtotal = selectedPurchase.total + shippingCost;

      const optionDetails = selectedPurchase?.paymentOptions?.find(
        (option) => option.method === selectedPaymentOption
      );

      if (optionDetails && optionDetails.details) {
        const feeType = optionDetails.feeType || '%';
        const feeValue = parseFloat(optionDetails.fee || 0);

        let calculatedFee = 0;
        if (feeType === '%') {
          calculatedFee = (feeValue / 100) * subtotal;
        } else {
          calculatedFee = feeValue;
        }

        setPaymentFee(calculatedFee);
        setTotalCost(subtotal + calculatedFee);
      } else {
        setPaymentFee(0);
        setTotalCost(subtotal);
      }
    }
  }, [shippingCost, selectedPurchase, selectedPaymentOption]);

  useEffect(() => {
    if (selectedPurchase) {
      const subtotal = selectedPurchase.total + shippingCost;

      const optionDetails = selectedPurchase?.paymentOptions?.find(
        (option) => option.method === selectedPaymentOption
      );

      if (optionDetails && optionDetails.details) {
        const feeType = optionDetails.feeType || '%';
        const feeValue = parseFloat(optionDetails.fee || 0);

        let calculatedFee = 0;
        if (feeType === '%') {
          calculatedFee = (feeValue / 100) * subtotal;
        } else {
          calculatedFee = feeValue;
        }

        setPaymentFee(calculatedFee);
        setTotalCost(subtotal + calculatedFee);
      } else {
        setPaymentFee(0);
        setTotalCost(subtotal);
      }
    }
  }, [shippingCost, selectedPurchase, selectedPaymentOption]);

  useEffect(() => {
    if (selectedPurchase) {
      // Reset relevant states when a new purchase is selected
      setPaymentDetails('');
      setSelectedPaymentOption('');
      setSelectedShippingOption('');
      setMessage('');
      setShippingCost(0);
      setPaymentFee(0);
      setTotalCost(0);
    }
  }, [selectedPurchase]);

 
  

  const fetchPurchases = useCallback(async () => {
    const purchasesSnapshot = await getDocs(collection(db, 'users', currentUser.uid, 'purchases'));
    const purchasesData = purchasesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  
    const sellerIds = [...new Set(purchasesData.map((purchase) => purchase.sellerId))];
    const fetchedUsernames = {};
  
    for (const sellerId of sellerIds) {
      const sellerDoc = await getDoc(doc(db, 'users', sellerId));
      if (sellerDoc.exists()) {
        fetchedUsernames[sellerId] = sellerDoc.data().username;
      }
    }
  
    setUsernames(fetchedUsernames);
    setPurchases(purchasesData);
    setLoading(false);
  }, [currentUser]);
  
  useEffect(() => {
    if (currentUser) {
      fetchPurchases();
    }
  }, [fetchPurchases, currentUser]);
  

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases, currentUser]);

  const fetchBuyerPreferences = async () => {
    const buyerDocRef = doc(db, 'users', currentUser.uid);
    const buyerDocSnap = await getDoc(buyerDocRef);

    if (buyerDocSnap.exists()) {
      return buyerDocSnap.data().buyerPreferences;
    } else {
      console.error('Buyer preferences not found');
      return null;
    }
  };

  
  

  const handleRequestDispute = async (disputeData) => {
    if (selectedPurchase) {
      const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', selectedPurchase.id);
      const saleRef = doc(db, 'users', selectedPurchase.sellerId, 'sales', selectedPurchase.id);
  
      const updatedDisputeData = {
        ...disputeData,
        status: 'Dispute Requested',
      };
  
      await updateDoc(purchaseRef, updatedDisputeData);
      await updateDoc(saleRef, updatedDisputeData);
  
      setMessageBoxContent({
        title: 'Dispute Requested',
        message: 'The dispute has been successfully requested.',
      });
      setMessageBoxOpen(true);
      fetchPurchases();
    }
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason('');
  };

  const handleClosePutBackInCartDialog = () => {
    setPutBackInCartDialogOpen(false);
    setCancelReason('');
  };

  const handleRequestRefund = async () => {
    if (selectedPurchase) {
      const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', selectedPurchase.id);
      const saleRef = doc(db, 'users', selectedPurchase.sellerId, 'sales', selectedPurchase.id);
  
      await updateDoc(purchaseRef, {
        status: 'Refund Requested',
        refundReason,
        refundPaymentMethod,
        refundPaymentDetails,
      });
  
      await updateDoc(saleRef, {
        status: 'Refund Requested',
        refundReason,
        refundPaymentMethod,
        refundPaymentDetails,
      });
  
      setRefundDialogOpen(false);
      setRefundReason('');
      setRefundPaymentDetails({});
      setMessageBoxContent({
        title: 'Refund Requested',
        message: 'The refund request has been successfully submitted.',
      });
      setMessageBoxOpen(true);
      fetchPurchases();
    }
  };

  const handleCloseRefundDialog = () => {
    setRefundDialogOpen(false);
    setRefundReason('');
    setRefundPaymentDetails({});
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleGetInvoice = async () => {
    const transactionDate = new Date(); 
    if (selectedPurchase) {
      const buyerPreferences = await fetchBuyerPreferences();

      if (buyerPreferences) {
        const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', selectedPurchase.id);
        const saleRef = doc(db, 'users', selectedPurchase.sellerId, 'sales', selectedPurchase.id);

        const sellerDoc = await getDoc(doc(db, 'users', selectedPurchase.sellerId));
        const sellerDetails = sellerDoc.exists() ? sellerDoc.data() : {};

        const updatedPurchaseData = {
          status: 'Invoice Generated',
          buyerPaymentMessage: message,
          buyerAddress: buyerPreferences,
          shippingCost,
          shippingOption: selectedShippingOption,
          paymentOption: { method: selectedPaymentOption, details: paymentDetails },
          totalCost,
          paymentFee,
          transactionDate,
          sellerDetails: {
            firstName: sellerDetails.firstName || 'Seller',
            lastName: sellerDetails.lastName || '',
            email: sellerDetails.email || '',
            phoneNumber: sellerDetails.phoneNumber || ''
          }
        };

        await updateDoc(purchaseRef, updatedPurchaseData);
        await updateDoc(saleRef, updatedPurchaseData);

        await fetchPurchases();

        setSelectedPurchase((prev) => ({ ...prev, ...updatedPurchaseData }));

        setPaymentDialogOpen(false);
        setInvoiceDialogOpen(true);
      } else {
        setMessageBoxContent({
          title: 'Attention Required',
          message: 'Failed to retrieve buyer preferences, please set your preferences in the Profile page.',
        });
        setMessageBoxOpen(true);
      }
    }
  };



  const handleEscalateDispute = async () => {
    if (selectedPurchase) {
      const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', selectedPurchase.id);
      const saleRef = doc(db, 'users', selectedPurchase.sellerId, 'sales', selectedPurchase.id);
  
      await updateDoc(purchaseRef, { status: 'Dispute Escalated' });
      await updateDoc(saleRef, { status: 'Dispute Escalated' });
  
      setMessageBoxContent({
        title: 'Dispute Escalated',
        message: 'The dispute has been escalated to support.',
      });
      setMessageBoxOpen(true);
      setViewDisputeDialogOpen(false);
      fetchPurchases();
    }
  };
  
  
  const handleAcceptResolution = async () => {
    if (selectedPurchase) {
      const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', selectedPurchase.id);
      const saleRef = doc(db, 'users', selectedPurchase.sellerId, 'sales', selectedPurchase.id);
  
      await updateDoc(purchaseRef, { status: 'Received' });
      await updateDoc(saleRef, { status: 'Received' });
  
      setMessageBoxContent({
        title: 'Resolution Accepted',
        message: 'The dispute has been resolved. Please rate the seller to complete the transaction.',
      });
      setMessageBoxOpen(true);
      setViewDisputeDialogOpen(false);
      fetchPurchases();
    }
  };
  

  const handleMarkPaid = (purchase) => {
    setSelectedPurchase(purchase);
    setMarkAsPaidDialogOpen(true);
  };
  
  
  const confirmMarkAsPaid = async (purchaseId, sellerId) => {
    const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', purchaseId);
    const saleRef = doc(db, 'users', sellerId, 'sales', purchaseId);
  
    await updateDoc(purchaseRef, { status: 'Paid', buyerPaymentMessage });
    await updateDoc(saleRef, { status: 'Paid', buyerPaymentMessage });
  
    setMessageBoxContent({
      title: 'Status Updated',
      message: 'The status has been updated to Paid, and the message has been sent to the seller.',
    });
    setMessageBoxOpen(true);
    fetchPurchases();
  };
  
  

  const handleMarkReceived = async (purchaseId, sellerId) => {
    try {
      const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', purchaseId);
      const saleRef = doc(db, 'users', sellerId, 'sales', purchaseId);
  
      await updateDoc(purchaseRef, { status: 'Received' });
      await updateDoc(saleRef, { status: 'Received' });
  
      setMessageBoxContent({
        title: 'Status Updated',
        message: 'The status has been updated to Received.',
      });
      setMessageBoxOpen(true);
      
      // Close the tracking dialog after updating the status
      setTrackingDialogOpen(false);
  
      fetchPurchases();
    } catch (error) {
      console.error("Error marking purchase as received: ", error);
    }
  };

  const handleApproveChanges = async (purchaseId, sellerId) => {
    try {
      const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', purchaseId);
      const saleRef = doc(db, 'users', sellerId, 'sales', purchaseId);
  
      await updateDoc(purchaseRef, { status: 'Buyer Approved' });
      await updateDoc(saleRef, { status: 'Buyer Approved' });
  
      setMessageBoxContent({
        title: 'Status Updated',
        message: 'The status has been updated to Buyer Approved.',
      });
      setMessageBoxOpen(true);
      
      // Close the tracking dialog after updating the status
      setApproveChangesDialogOpen(false);
  
      fetchPurchases();
    } catch (error) {
      console.error("Error marking purchase as changes approved: ", error);
    }
  };
  
  
  

  const handleMarkRated = async (purchaseId, sellerId) => {
    const purchaseRef = doc(db, 'users', currentUser.uid, 'purchases', purchaseId);
    const saleRef = doc(db, 'users', sellerId, 'sales', purchaseId);
  
    // Delete images upon sale completion
   // const saleDoc = await getDoc(saleRef);
   // if (saleDoc.exists()) {
    //  const saleData = saleDoc.data();
   //   const imageUrls = saleData.imageUrls || [];
    //  for (const url of imageUrls) {
   //     const fileRef = ref(storage, url);
   //     await deleteObject(fileRef);
   //   }
  //   await updateDoc(saleRef, { imageUrls: [] });
   // }
  
    await updateDoc(purchaseRef, { status: 'Completed' });
    await updateDoc(saleRef, { status: 'Completed' });
  
    setMessageBoxContent({
      title: 'Status Updated',
      message: 'The status has been updated to Completed.',
    });
    setMessageBoxOpen(true);
    fetchPurchases();
    setRateSellerDialogOpen(false);
  };
  
  

  const renderRefundPaymentDetailsFields = () => {
    switch (refundPaymentMethod) {
      case 'Bank Transfer':
        return (
          <Box>
            <TextField
              label="Account Name"
              fullWidth
              value={refundPaymentDetails.accountName || ''}
              onChange={(e) =>
                setRefundPaymentDetails((prev) => ({
                  ...prev,
                  accountName: e.target.value,
                }))
              }
              variant="outlined"
              margin="normal"
            />
            <TextField
              label="Account Number"
              fullWidth
              value={refundPaymentDetails.accountNumber || ''}
              onChange={(e) =>
                setRefundPaymentDetails((prev) => ({
                  ...prev,
                  accountNumber: e.target.value,
                }))
              }
              variant="outlined"
              margin="normal"
            />
            <TextField
              label="BSB"
              fullWidth
              value={refundPaymentDetails.bsb || ''}
              onChange={(e) =>
                setRefundPaymentDetails((prev) => ({
                  ...prev,
                  bsb: e.target.value,
                }))
              }
              variant="outlined"
              margin="normal"
            />
          </Box>
        );
      case 'Paypal':
        return (
          <TextField
            label="Paypal ID"
            fullWidth
            value={refundPaymentDetails.paypalID || ''}
            onChange={(e) =>
              setRefundPaymentDetails((prev) => ({
                ...prev,
                paypalID: e.target.value,
              }))
            }
            variant="outlined"
            margin="normal"
          />
        );
      case 'PayID':
        return (
          <TextField
            label="PayID"
            fullWidth
            value={refundPaymentDetails.payID || ''}
            onChange={(e) =>
              setRefundPaymentDetails((prev) => ({
                ...prev,
                payID: e.target.value,
              }))
            }
            variant="outlined"
            margin="normal"
          />
        );
      case 'Other':
        return (
          <TextField
            label="Other Payment Details"
            fullWidth
            multiline
            rows={4}
            value={refundPaymentDetails.otherDetails || ''}
            onChange={(e) =>
              setRefundPaymentDetails((prev) => ({
                ...prev,
                otherDetails: e.target.value,
              }))
            }
            variant="outlined"
            margin="normal"
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        My Purchases
      </Typography>

      <Button
      variant="outlined"
      color="primary"
      onClick={handleNavigateToPurchaseSummary}
    >
      View Purchase Summary
    </Button>

      <OfferTracking currentUser={currentUser} />

      <PurchaseList
        purchases={purchases}
        usernames={usernames}
        searchTerm={searchTerm}
        sortOption={sortOption}
        onSearchChange={setSearchTerm}
        onSortChange={setSortOption}
        allCollapsed={allCollapsed}
        onToggleCollapse={setAllCollapsed}
        onOpenProfile={handleOpenProfile}
        onOpenCancelDialog={handleOpenCancelDialog}
        onOpenRefundDialog={handleOpenRefundDialog}
        onOpenDisputeDialog={handleOpenDisputeDialog}
        onOpenInvoiceDialog={handleOpenInvoiceDialog}
        onOpenTrackingDialog={handleOpenTrackingDialog}
        onOpenApproveChangesDialog={handleOpenApproveChangesDialog}
        onOpenPaymentDialog={handleOpenPaymentDialog}
        onOpenImageViewer={handleOpenImageViewer}
        onMarkAsPaid={handleMarkPaid}
        onRateSeller={(purchase) => {
          setSelectedPurchase(purchase); // Set the selected purchase
          setRateSellerDialogOpen(true); // Open the dialog
        }}
        onPutBackInCart={handleOpenPutBackInCartDialog}
        onOpenPaymentDetailsDialog={handleOpenPaymentDetailsDialog}
      />

      {/* Cancel Purchase Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog}>
        <DialogTitle>Cancel Purchase</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for Cancellation"
            fullWidth
            multiline
            rows={4}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            variant="outlined"
            margin="normal"
          />
          <DialogActions>
            <Button onClick={handleCloseCancelDialog} color="secondary">
              Close
            </Button>
            <Button onClick={handleCancelPurchase} color="primary">
              Confirm Cancellation
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Put Back In Cart Dialog */}
      <Dialog
        open={putBackInCartDialogOpen}
        onClose={handleClosePutBackInCartDialog}
      >
        <DialogTitle>
          Are you sure you want to put items back in Cart?
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">
            Press Continue to put this purchase back in your Cart so you can
            add/modify items.
          </Typography>
          <DialogActions>
            <Button onClick={handleClosePutBackInCartDialog} color="secondary">
              Close
            </Button>
            <Button onClick={handlePutBackInCart} color="primary">
              Continue
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Request Refund Dialog */}
      <Dialog open={refundDialogOpen} onClose={handleCloseRefundDialog}>
        <DialogTitle>Request Refund</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for Refund"
            fullWidth
            multiline
            rows={4}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            variant="outlined"
            margin="normal"
          />
          <FormControl component="fieldset" fullWidth margin="normal">
            <Typography variant="subtitle1">Refund Payment Method</Typography>
            <RadioGroup
              value={refundPaymentMethod}
              onChange={(e) => setRefundPaymentMethod(e.target.value)}
            >
              <FormControlLabel
                value="Bank Transfer"
                control={<Radio />}
                label="Bank Transfer"
              />
              <FormControlLabel
                value="Paypal"
                control={<Radio />}
                label="Paypal"
              />
              <FormControlLabel
                value="PayID"
                control={<Radio />}
                label="PayID"
              />
              <FormControlLabel
                value="Other"
                control={<Radio />}
                label="Other"
              />
            </RadioGroup>
          </FormControl>
          {renderRefundPaymentDetailsFields()}
          <DialogActions>
            <Button onClick={handleCloseRefundDialog} color="secondary">
              Close
            </Button>
            <Button onClick={handleRequestRefund} color="primary">
              Confirm Refund Request
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* View Dispute Details Dialog */}
      <Dialog
        open={viewDisputeDialogOpen}
        onClose={() => setViewDisputeDialogOpen(false)}
      >
        <DialogTitle>Dispute Details</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">Reason for Dispute</Typography>
          <Typography variant="body2">
            {selectedPurchase?.disputeReason || "No reason provided."}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Seller Response
          </Typography>
          <Typography variant="body2">
            {selectedPurchase?.sellerResponse || "No response provided."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewDisputeDialogOpen(false)}
            color="secondary"
          >
            Close
          </Button>
          <Button onClick={handleEscalateDispute} color="warning">
            Escalate
          </Button>
          <Button onClick={handleAcceptResolution} color="primary">
            Accept Resolution
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispute Purchase Dialog */}
      <BuyerDispute
        open={disputeDialogOpen}
        onClose={() => setDisputeDialogOpen(false)}
        onSubmit={handleRequestDispute}
        purchase={selectedPurchase}
      />

      <PaymentDetailsDialog
        open={paymentDetailsDialogOpen}
        onClose={handleClosePaymentDetailsDialog}
        paymentOption={selectedPurchase?.paymentOption}
        total={selectedPurchase?.total}
        shippingCost={selectedPurchase?.shippingCost}
        paymentFee={selectedPurchase?.paymentFee}
        totalCost={selectedPurchase?.totalCost}
      />

      <PaymentRequestedDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        purchase={selectedPurchase}
        buyerAddress={buyerAddress}
        setBuyerAddress={setBuyerAddress}
        fetchBuyerPreferences={fetchBuyerPreferences}
        handleGetInvoice={handleGetInvoice}
        message={message}
        setMessage={setMessage}
        selectedShippingOption={selectedShippingOption}
        setSelectedShippingOption={setSelectedShippingOption}
        selectedPaymentOption={selectedPaymentOption}
        setSelectedPaymentOption={setSelectedPaymentOption}
        paymentDetails={paymentDetails}
        setPaymentDetails={setPaymentDetails}
        paymentFee={paymentFee}
        totalCost={totalCost}
        handleCancelPurchase={handleCancelPurchase}
      />

      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent>
          <Invoice
            sale={selectedPurchase}
            onClose={() => setInvoiceDialogOpen(false)}
            shippingOption={selectedPurchase?.shippingOption}
            paymentOption={selectedPurchase?.paymentOption}
            totalCost={totalCost}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={trackingDialogOpen}
        onClose={() => setTrackingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Tracking Information</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">
            Shipping Company: {selectedPurchase?.shippingCompany || "N/A"}
          </Typography>
          <Typography variant="subtitle1">
            Tracking Number: {selectedPurchase?.trackingNumber || "N/A"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTrackingDialogOpen(false)}
            color="secondary"
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              handleMarkReceived(selectedPurchase.id, selectedPurchase.sellerId)
            }
          >
            Mark as Received
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveChangesDialogOpen}
        onClose={() => setApproveChangesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Price/Quantity Change</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">
            If you have reviewed the changes and you are happy to proceed with
            the purchase, press continue, otherwise return to the sale and press
            the cancel purchase button on the item
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveChangesDialogOpen(false)}
            color="secondary"
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              handleApproveChanges(
                selectedPurchase.id,
                selectedPurchase.sellerId
              )
            }
          >
            Approve Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUserId && <PublicProfile userId={selectedUserId} />}{" "}
          {/* Pass the selectedUserId */}
        </DialogContent>
      </Dialog>

      <Dialog
        open={markAsPaidDialogOpen}
        onClose={() => setMarkAsPaidDialogOpen(false)}
      >
        <DialogTitle>Mark as Paid</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">
            Total Owing: ${selectedPurchase?.totalCost || "N/A"}
          </Typography>
          <Typography variant="body1" gutterBottom>
            Do you want to send a message to the seller?
          </Typography>
          <TextField
            label="Message to Seller (Optional)"
            fullWidth
            multiline
            rows={4}
            value={buyerPaymentMessage}
            onChange={(e) => setBuyerPaymentMessage(e.target.value)}
            variant="outlined"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMarkAsPaidDialogOpen(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await confirmMarkAsPaid(
                selectedPurchase.id,
                selectedPurchase.sellerId
              );
              setMarkAsPaidDialogOpen(false);
            }}
            color="primary"
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openOfferTracking}
        onClose={handleCloseOfferTracking}
        fullWidth
      >
        <DialogTitle>Track Offers</DialogTitle>
        <DialogContent>
          <OfferTracking currentUser={currentUser} />
        </DialogContent>
      </Dialog>

      <RateSellerDialog
        open={rateSellerDialogOpen}
        onClose={() =>
          handleMarkRated(selectedPurchase.id, selectedPurchase.sellerId)
        }
        sale={selectedPurchase}
      />
      {selectedPurchase && (
        <ImageViewerDialog
          open={imageDialogOpen}
          onClose={handleCloseImageViewer}
          saleId={selectedPurchase.id}
          sellerId={selectedPurchase.sellerId}
        />
      )}
      <MessageBox
        open={messageBoxOpen}
        title={messageBoxContent.title}
        message={messageBoxContent.message}
        onClose={() => setMessageBoxOpen(false)}
      />
    </Container>
  );
};

export default BuyerPurchases;
